import type { Namespace, TFunction } from "i18next";
import { z } from "zod";

import { EXTENSIONS_BY_MIME_TYPE } from "../file";
import { Case, createTc, Tk, TranslationParams } from "../i18n";
import { capitalize, COMMA_WITH_SPACE } from "../str";

import { isInvalidDateIssue } from "./date";
import { isInvalidDateTimeIssue } from "./date-time";
import { isMimeTypeIssue } from "./file";
import { isRegexIssue } from "./regex";

function errorMapForForms<Ns extends Namespace, KPrefix>(t: TFunction<Ns, KPrefix>) {
  const tc = createTc(t);
  const map: z.ZodErrorMap = (issue, ctx) => {
    const { code } = issue;

    if (code === z.ZodIssueCode.invalid_type) {
      const { expected } = issue;

      if (expected === "number") {
        return {
          message: tc(Tk.MUST_BE_A_NUMBER),
        };
      }
    }

    if (code === z.ZodIssueCode.invalid_string) {
      const { validation } = issue;
      if (typeof validation === "string") {
        return {
          message: tc(Tk.DOES_NOT_MATCH_FORMAT, {
            format: translateFormat(t, validation),
          }),
        };
      }
    }

    if (code === z.ZodIssueCode.too_small) {
      const { inclusive, minimum, type, exact } = issue;
      if (type === "string" && !exact) {
        const exactMinimum = Number(inclusive ? minimum : Number(minimum) + 1);
        return {
          message: tc(Tk.NOT_ENOUGH_CHARACTERS_AT_LEAST_N_REQUIRED, {
            count: exactMinimum,
          }),
        };
      } else if (type === "number") {
        return {
          message: tc(inclusive ? Tk.MUST_BE_AT_LEAST_N : Tk.MUST_BE_GREATER_THAN_N, {
            count: Number(minimum),
          }),
        };
      }
    }

    return {
      message: ctx.defaultError,
    };
  };

  return map;
}

function errorMapForForms2<Ns extends Namespace, KPrefix>(t: TFunction<Ns, KPrefix>) {
  const tc = createTc(t);
  const map: z.ZodErrorMap = (issue, ctx) => {
    const { code } = issue;

    if (code === z.ZodIssueCode.invalid_type) {
      const { expected, received } = issue;

      if (received === "undefined") {
        return {
          message: tc(Tk.FIELD_CANNOT_BE_EMPTY),
        };
      }

      if (expected === "number") {
        return {
          message: tc(Tk.FIELD_MUST_CONTAIN_A_NUMBER),
        };
      }
    }

    if (code === z.ZodIssueCode.invalid_string) {
      const { validation } = issue;
      if (typeof validation === "string") {
        return {
          message: tc(Tk.FIELD_MUST_MATCH_FORMAT, {
            format: translateFormat(t, validation),
          }),
        };
      }
    }

    if (code === z.ZodIssueCode.too_small) {
      const { inclusive, minimum, type, exact } = issue;
      if (type === "string" && !exact) {
        const exactMinimum = Number(inclusive ? minimum : Number(minimum) + 1);
        return {
          message: tc(
            exactMinimum === 1
              ? Tk.FIELD_CANNOT_BE_EMPTY
              : Tk.FIELD_MUST_CONTAIN_AT_LEAST_N_CHARACTERS,
            {
              count: exactMinimum,
            },
          ),
        };
      } else if (type === "number") {
        return {
          message: tc(inclusive ? Tk.MINIMUM_VALUE_IS_N : Tk.VALUE_MUST_BE_GREATER_THAN_N, {
            count: Number(minimum),
          }),
        };
      }
    }

    if (code === z.ZodIssueCode.too_big) {
      const { inclusive, maximum, type, exact } = issue;
      if (type === "string" && !exact) {
        const exactMaximum = Number(inclusive ? maximum : Number(maximum) - 1);
        return {
          message: tc(Tk.FIELD_MUST_CONTAIN_NO_MORE_THAN_N_CHARACTER, {
            count: exactMaximum,
          }),
        };
      } else if (type === "number") {
        return {
          message: tc(inclusive ? Tk.MAXIMUM_VALUE_IS_N : Tk.VALUE_MUST_BE_LESS_THAN_N, {
            count: Number(maximum),
          }),
        };
      }
    }

    if (isInvalidDateIssue(issue)) {
      const { errorCode } = issue.params;
      const TK_BY_ERROR_CODE: Record<typeof errorCode, Tk> = {
        DATE_MUST_NOT_BE_IN_THE_PAST: Tk.THE_DATE_MUST_NOT_BE_IN_THE_PAST,
      };

      return {
        message: capitalize(t(TK_BY_ERROR_CODE[errorCode])),
      };
    }

    if (isInvalidDateTimeIssue(issue)) {
      const { errorCode } = issue.params;
      const TK_BY_ERROR_CODE: Record<typeof errorCode, Tk> = {
        DATE_TIME_MUST_NOT_BE_IN_THE_PAST: Tk.THE_DATE_MUST_NOT_BE_EARLIER_THAN_THE_CURRENT_MOMENT,
      };

      return {
        message: capitalize(t(TK_BY_ERROR_CODE[errorCode])),
      };
    }

    if (isRegexIssue(issue)) {
      const { regexCode } = issue.params;
      const TK_BY_REGEX_CODE: Record<typeof regexCode, Tk> = {
        MUST_MATCH_PHONE_NUMBER_FORMAT: Tk.VALUE_MUST_MATCH_PHONE_NUMBER_FORMAT,
      };

      return {
        message: capitalize(t(TK_BY_REGEX_CODE[regexCode])),
      };
    }

    if (isMimeTypeIssue(issue)) {
      const {
        params: { cause },
      } = issue;
      if (cause.code === z.ZodIssueCode.invalid_enum_value) {
        const { options } = cause;
        return {
          message: tc(Tk.ONLY_IMAGES_IN_JPG_PNG_SVG_FORMATS_ARE_ACCEPTED, {
            formats: options
              .map((value) =>
                (EXTENSIONS_BY_MIME_TYPE as Record<string, string>)[value.toString()]
                  ?.slice(1)
                  .toLocaleUpperCase(),
              )
              .filter((value) => value !== undefined)
              .join(COMMA_WITH_SPACE),
          }),
        };
      }
    }

    return {
      message: ctx.defaultError,
    };
  };

  return map;
}

const FORMAT_BY_VALIDATION: Partial<
  Record<Extract<z.StringValidation, string>, TranslationParams.Type>
> = {
  email: {
    key: Tk.EMAIL,
    options: {
      context: Case.genitive,
    },
  },
  url: Tk.HYPERLINK,
};

function translateFormat<Ns extends Namespace, KPrefix>(
  t: TFunction<Ns, KPrefix>,
  validation: Extract<z.StringValidation, string>,
) {
  const format = FORMAT_BY_VALIDATION[validation];

  return format ? t(...TranslationParams.toTuple(format)) : validation;
}

export { errorMapForForms, errorMapForForms2 };
